import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RouterIcon from '@mui/icons-material/Router';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

import type {
  IMigracionOltEstadoFinal,
  IMigracionOltResultadoMigracion,
} from '@/shared/actions/erp/tickets-soporte-cliente/softnet-import.actions';

/**
 * Bitácora de una migración, cliente por cliente.
 *
 * Vive aparte porque la usan dos pantallas: el tab de Migración para la corrida
 * en curso, y el de Historial para retomar lo que quedó pendiente de corridas
 * anteriores. Tenerla en el tab y que el Historial la importara de ahí sería un
 * ciclo de imports, porque el tab ya importa al Historial.
 */

/** Segundos con un decimal. */
export const seg = (ms?: number | null) => `${((ms ?? 0) / 1000).toFixed(1)}s`;

/** Bloque de JSON crudo, monoespaciado y con scroll. */
export const BloqueJson: React.FC<{ titulo: string; valor: unknown }> = ({ titulo, valor }) => (
  <Box sx={{ mb: 1 }}>
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}
    >
      {titulo}
    </Typography>
    <Box
      component="pre"
      sx={{
        m: 0,
        mt: 0.5,
        p: 1.25,
        maxHeight: 260,
        overflow: 'auto',
        fontFamily: 'monospace',
        fontSize: 12,
        lineHeight: 1.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
        border: '1px solid',
        borderColor: 'divider',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {valor === null || valor === undefined
        ? '(sin contenido)'
        : JSON.stringify(valor, null, 2)}
    </Box>
  </Box>
);

export const ESTADO_FINAL_CFG: Record<
  IMigracionOltEstadoFinal,
  { label: string; color: 'success' | 'warning' | 'default' | 'error'; ayuda: string }
> = {
  COMPLETADO: {
    label: 'Migrado',
    color: 'success',
    ayuda: 'Se desconfiguró y se volvió a aprovisionar correctamente.',
  },
  COMPLETADO_AVISO: {
    label: 'Migrado · ACS pendiente',
    color: 'success',
    ayuda:
      'El servicio quedó activo: la OLT devolvió potencia y service-port. Lo único pendiente es que el ACS tome la configuración del ONT (hasta 20 min, se resuelve solo). No hay nada que reintentar.',
  },
  FALLO_CANCELAR: {
    label: 'No se desconfiguró',
    color: 'default',
    ayuda: 'No se pudo desconfigurar tras 3 intentos. El cliente sigue como estaba: nadie quedó sin servicio.',
  },
  FALLO_ACTIVAR: {
    label: 'SIN SERVICIO',
    color: 'error',
    ayuda: 'Se desconfiguró pero NO se pudo activar. El cliente está sin internet: hay que entrar a la OLT.',
  },
  RECHAZADO: {
    label: 'Rechazado',
    color: 'default',
    ayuda: 'No pasó la validación, así que no se tocó la OLT.',
  },
};


export const PASO_LLAMADA_CFG: Record<
  'CONSULTAR' | 'CANCELAR' | 'ACTIVAR',
  { label: string; detalle: string }
> = {
  CONSULTAR: {
    label: '1 · Consultar',
    detalle: 'cancelarSinValidacion con cancelar_servicios: false — lee la configuración actual',
  },
  CANCELAR: {
    label: '2 · Desconfigurar',
    detalle: 'cancelarSinValidacion con cancelar_servicios: true — desde aquí el cliente queda sin servicio',
  },
  ACTIVAR: {
    label: '3 · Activar',
    detalle: 'activarSinValidacion — reaprovisiona la ONT',
  },
};


/**
 * Bitácora de UN cliente: las tres llamadas con su body y su respuesta en JSON.
 *
 * Se muestra como JSON crudo y no como tabla porque lo que importa aquí es poder
 * ver exactamente qué se mandó y qué contestó la OLT, campo por campo, para
 * reconciliar si algo salió mal. La clave PPPoE llega enmascarada del backend.
 */
export interface BitacoraClienteProps {
  item: IMigracionOltResultadoMigracion;
  /** Solo se pasa si el usuario puede ejecutar; sin esto no hay botón. */
  onReintentar?: (item: IMigracionOltResultadoMigracion) => void;
  reintentando?: boolean;
}

/**
 * Lectura de una entrada: qué decirle al operador de un vistazo.
 *
 * Se calcula acá y no se lee directo de ESTADO_FINAL_CFG porque
 * COMPLETADO_AVISO tiene dos lecturas distintas: si el micro confirmó la
 * activación es un éxito con una nota (el ACS), y si respondió 200 con un
 * mensaje que no confirma nada es un caso ambiguo que alguien debe mirar.
 */
const lectura = (item: IMigracionOltResultadoMigracion) => {
  const cfg = ESTADO_FINAL_CFG[item.estado_final];
  const ambiguo = item.estado_final === 'COMPLETADO_AVISO' && item.activar_ok === false;
  const sinServicio = item.estado_final === 'FALLO_ACTIVAR';
  const bien = item.estado_final === 'COMPLETADO' || (item.estado_final === 'COMPLETADO_AVISO' && !ambiguo);
  if (ambiguo) {
    return {
      label: 'Revisar',
      color: 'warning' as const,
      ayuda:
        'El microservicio respondió 200 pero el mensaje no confirma la activación. Hay que verificar en la OLT.',
      bien: false,
      sinServicio: false,
    };
  }
  return { label: cfg.label, color: cfg.color, ayuda: cfg.ayuda, bien, sinServicio };
};

export const BitacoraCliente: React.FC<BitacoraClienteProps> = ({
  item,
  onReintentar,
  reintentando = false,
}) => {
  const cfg = lectura(item);
  // Se abre solo lo que pide atención. Antes se abría todo lo que tuviera
  // requiere_revision, y con el aviso del ACS eso eran decenas de tarjetas.
  const [abierto, setAbierto] = useState(cfg.sinServicio || cfg.color === 'warning');

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1.5,
        borderColor: cfg.sinServicio ? 'error.main' : 'divider',
        borderWidth: cfg.sinServicio ? 2 : 1,
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ p: 1.25, cursor: 'pointer' }}
        onClick={() => setAbierto((v) => !v)}
      >
        <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{item.serie}</Typography>
        <Tooltip arrow title={cfg.ayuda}>
          <Chip
            size="small"
            label={cfg.label}
            color={cfg.color}
            icon={
              cfg.bien ? <CheckCircleIcon /> : cfg.sinServicio ? <ErrorIcon /> : undefined
            }
          />
        </Tooltip>
        <Typography variant="body2" color="text.secondary">
          {item.nombre_cliente ?? '—'}
        </Typography>
        {(item.llamadas ?? []).map((l, i) => (
          <Chip
            key={`${l.paso}-${i}`}
            size="small"
            variant="outlined"
            color={l.ok ? 'success' : 'error'}
            label={`${PASO_LLAMADA_CFG[l.paso]?.label ?? l.paso}${l.intento > 1 ? ` (#${l.intento})` : ''}`}
          />
        ))}

        {/* Lo que se rescató aunque no se migrara: la consulta previa dejó el
            puerto, los perfiles y los service-port guardados, y eso es lo que
            hace falta para aprovisionar después. */}
        {item.info_capturada && (
          <Tooltip
            arrow
            title="La consulta dejó guardada la configuración de la ONT; sirve para aprovisionar después"
          >
            <Chip size="small" color="info" variant="outlined" label="info guardada" />
          </Tooltip>
        )}

        {item.aprovisionamiento_ok === true && (
          <Tooltip arrow title={item.aprovisionamiento_mensaje ?? 'Escrito en aprovisionamiento'}>
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label={`aprovisionado${item.aprovisionamiento_contrato ? ` · ${item.aprovisionamiento_contrato}` : ''}`}
            />
          </Tooltip>
        )}
        {item.aprovisionamiento_ok === false && (
          <Tooltip arrow title={item.aprovisionamiento_mensaje ?? 'No se pudo ligar'}>
            <Chip size="small" color="warning" variant="outlined" label="sin ligar" />
          </Tooltip>
        )}
        {item.cedula && (
          <Tooltip
            arrow
            title={`Cédula resuelta desde ${item.cedula_origen === "SOFTNET" ? "Softnet" : "el catálogo del ERP"}`}
          >
            <Chip size="small" variant="outlined" label={`CI ${item.cedula}`} />
          </Tooltip>
        )}
        {item.cliente_creado && (
          <Tooltip arrow title="El cliente no estaba en tickets y se importó de Softnet">
            <Chip size="small" variant="outlined" label="cliente importado" />
          </Tooltip>
        )}
        {/* Con qué service profile entró. Solo se muestra cuando NO fue el
            que resolvió el sistema: ahí es dato, porque significa que el
            modelo real no lo acepta el microservicio. */}
        {item.service_profile_usado && item.service_profile_origen !== 'SISTEMA' && (
          <Tooltip
            arrow
            title={
              item.service_profile_origen === 'GENERICO'
                ? 'El modelo del equipo no está en el catálogo, así que se activó con el comodín. Conviene registrar el modelo real.'
                : 'El modelo del sistema no entró; se activó con el nombre del catálogo.'
            }
          >
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={`SP ${item.service_profile_usado}`}
            />
          </Tooltip>
        )}
        {(item.reintentos_activacion ?? 0) > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={`${item.reintentos_activacion} reintento(s)`}
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {seg(item.duracion_ms)}
        </Typography>
        <Button size="small">{abierto ? 'Ocultar JSON' : 'Ver JSON'}</Button>
      </Stack>

      {abierto && (
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          {/* El aviso del microservicio, como nota. Se deja el texto tal cual
              lo manda, pero sin pintarlo de rojo: cuando confirma la
              activación el cliente ya tiene servicio. */}
          {item.estado_final === 'COMPLETADO_AVISO' && item.activar_mensaje && (
            <Alert
              severity={cfg.bien ? 'success' : 'warning'}
              variant="outlined"
              sx={{ mb: 1.5, py: 0.5 }}
            >
              <AlertTitle sx={{ fontSize: 13, mb: 0 }}>
                {cfg.bien ? 'Activación validada · nota del microservicio' : 'Revisar'}
              </AlertTitle>
              {item.activar_mensaje}
            </Alert>
          )}

          {/* La observación explica en lenguaje de operador por qué no se migró
              y qué quedó guardado; el motivo_rechazo es el detalle técnico. */}
          {item.observacion && (
            <Alert
              severity={item.info_capturada ? 'success' : 'info'}
              variant="outlined"
              sx={{ mb: 1.5, py: 0.5 }}
            >
              {item.observacion}
            </Alert>
          )}
          {item.motivo_rechazo && (
            <Alert severity="info" variant="outlined" sx={{ mb: 1.5, py: 0.5 }}>
              {item.motivo_rechazo}
            </Alert>
          )}

          {item.aprovisionamiento_mensaje && (
            <Alert
              severity={item.aprovisionamiento_ok ? 'success' : 'warning'}
              variant="outlined"
              sx={{ mb: 1.5, py: 0.5 }}
            >
              <AlertTitle sx={{ fontSize: 13, mb: 0 }}>Aprovisionamiento</AlertTitle>
              {item.aprovisionamiento_mensaje}
            </Alert>
          )}

          {/* Reintento manual: solo para quien quedó sin servicio. Repite SOLO
              la activación (la ONT ya está desconfigurada) y acumula el
              resultado sobre esta misma entrada. */}
          {item.estado_final === 'FALLO_ACTIVAR' && onReintentar && (
            <Alert severity="error" variant="outlined" sx={{ mb: 1.5 }}>
              <AlertTitle>Este cliente está sin servicio</AlertTitle>
              La ONT quedó desconfigurada y la activación no entró. El reintento repite{' '}
              <strong>solo la activación</strong>; no vuelve a desconfigurar nada.
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  disableElevation
                  disabled={reintentando}
                  startIcon={
                    reintentando ? <CircularProgress size={16} color="inherit" /> : <RouterIcon />
                  }
                  onClick={() => onReintentar(item)}
                  sx={{ textTransform: 'none', minHeight: 44 }}
                >
                  {reintentando ? 'Reintentando...' : 'Reintentar la activación'}
                </Button>
              </Box>
            </Alert>
          )}


          {(item.llamadas ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No se registró ninguna llamada: no se tocó la OLT.
            </Typography>
          ) : (
            (item.llamadas ?? []).map((l, i) => (
              <Box
                key={`${l.paso}-${l.intento}-${i}`}
                sx={{ mb: 2, pl: 1.5 }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }} flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" fontWeight={700}>
                    {PASO_LLAMADA_CFG[l.paso]?.label ?? l.paso}
                    {l.intento > 1 && ` — intento ${l.intento}`}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={l.ok ? 'success' : 'error'}
                    label={l.ok ? 'OK' : 'falló'}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {seg(l.duracion_ms)} · {PASO_LLAMADA_CFG[l.paso]?.detalle}
                  </Typography>
                </Stack>

                {l.api && (
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ fontFamily: 'monospace', color: 'text.secondary', mb: 0.75 }}
                  >
                    {l.metodo ?? 'POST'} {l.api}
                  </Typography>
                )}

                {l.error && (
                  <Alert
                    severity={l.ok ? 'info' : 'error'}
                    variant="outlined"
                    sx={{ mb: 1, py: 0.5 }}
                  >
                    {l.error}
                  </Alert>
                )}

                <BloqueJson titulo="Body enviado" valor={l.request} />
                <BloqueJson titulo="Respuesta" valor={l.response} />
              </Box>
            ))
          )}
        </Box>
      )}
    </Paper>
  );
};
